import {
  IoBriefcase,
  IoBusinessOutline,
  IoLayersOutline,
  IoPeopleOutline,
} from "react-icons/io5";
import { fetchApplications, fetchJobs } from "../../redux/careers";
import styled, { keyframes } from "styled-components";
import { useDispatch, useSelector } from "react-redux";
import { useEffect, useMemo } from "react";

import Moment from "moment";
import { Section } from "../../components/section";
import { fetchServices } from "../../redux/services";
import { shadow } from "../../assets/styles/parameters";

export const AboutList = () => {
  const dispatch = useDispatch();

  // Pull all organizational data from Redux
  const { services } = useSelector(
    (state) => state.services || { services: [] },
  );
  const { jobs, applications } = useSelector(
    (state) => state.careers || { jobs: [], applications: [] },
  );

  // Fetch data on mount to ensure the summary is accurate
  useEffect(() => {
    dispatch(fetchServices());
    dispatch(fetchJobs());
    dispatch(fetchApplications());
  }, [dispatch]);

  // Aggregate Data for the View
  const { openJobs, recentApps, activeServices } = useMemo(() => {
    return {
      openJobs: jobs.filter((j) => j.status === "OPEN"),
      recentApps: applications.slice(0, 5), // Grab only the 5 most recent
      activeServices: services.slice(0, 5),
    };
  }, [jobs, applications, services]);

  return (
    <Section>
      {/* 1. HEADER SECTION */}
      <HeaderSection>
        <SubGreeting>
          A complete summary of your company profile, services, and career data.
        </SubGreeting>
      </HeaderSection>

      {/* 2. TOP LEVEL STATS */}
      <StatsGrid>
        <StatCard delay="0.1s">
          <StatHeader>
            <IconWrapper bg="#F4ECF7" color="#9B59B6">
              <IoBusinessOutline />
            </IconWrapper>
          </StatHeader>
          <StatValue>Active</StatValue>
          <StatLabel>Organization Status</StatLabel>
        </StatCard>

        <StatCard delay="0.2s">
          <StatHeader>
            <IconWrapper bg="#E6F2FF" color="#3B5BDB">
              <IoLayersOutline />
            </IconWrapper>
          </StatHeader>
          <StatValue>{services.length}</StatValue>
          <StatLabel>Total Services</StatLabel>
        </StatCard>

        <StatCard delay="0.3s">
          <StatHeader>
            <IconWrapper bg="#E8F8F5" color="#2ECA7F">
              <IoBriefcase />
            </IconWrapper>
          </StatHeader>
          <StatValue>{openJobs.length}</StatValue>
          <StatLabel>Open Positions</StatLabel>
        </StatCard>

        <StatCard delay="0.4s">
          <StatHeader>
            <IconWrapper bg="#FFF4E6" color="#F39C12">
              <IoPeopleOutline />
            </IconWrapper>
          </StatHeader>
          <StatValue>{applications.length}</StatValue>
          <StatLabel>Total Applications</StatLabel>
        </StatCard>
      </StatsGrid>

      {/* 3. MAIN DATA SPLIT */}
      <WidgetsGrid>
        {/* LEFT COLUMN: About & Services */}
        <Column>
          <WidgetCard delay="0.5s">
            <WidgetTitle>About the Organization</WidgetTitle>
            <AboutText>
              Oasis Healthcare is dedicated to providing top-tier medical and
              telehealth services. We bridge the gap between patients and
              specialized healthcare professionals through innovative technology
              and compassionate care.
            </AboutText>
            <MetaFlex>
              <MetaTag>Established: 2020</MetaTag>
              <MetaTag>Headquarters: Maryland, USA</MetaTag>
            </MetaFlex>
          </WidgetCard>

          <WidgetCard delay="0.6s">
            <WidgetTitle>Featured Services</WidgetTitle>
            <ListContainer>
              {activeServices.length > 0 ? (
                activeServices.map((service) => {
                  const cType = service.media?.[0]?.contentType || "";
                  let typeLabel = "TEXT ONLY";
                  if (cType.startsWith("image/")) typeLabel = "IMAGE";
                  else if (cType.startsWith("video/")) typeLabel = "VIDEO";
                  else if (cType) typeLabel = "FILE";

                  return (
                    <ListItem key={service.id}>
                      <ItemMain>{service.title}</ItemMain>
                      <ItemSub>{typeLabel}</ItemSub>
                    </ListItem>
                  );
                })
              ) : (
                <EmptyText>No services configured yet.</EmptyText>
              )}
            </ListContainer>
          </WidgetCard>
        </Column>

        {/* RIGHT COLUMN: Careers & Apps */}
        <Column>
          <WidgetCard delay="0.7s">
            <WidgetTitle>Recent Job Postings</WidgetTitle>
            <ListContainer>
              {openJobs.length > 0 ? (
                openJobs.slice(0, 4).map((job) => (
                  <ListItem key={job.id}>
                    <div>
                      <ItemMain>{job.title}</ItemMain>
                      <ItemSub>{job.jobType?.replace("_", " ")}</ItemSub>
                    </div>
                    <Badge color="#2ECA7F" bg="#E8F8F5">
                      {job.status}
                    </Badge>
                  </ListItem>
                ))
              ) : (
                <EmptyText>No open positions at this time.</EmptyText>
              )}
            </ListContainer>
          </WidgetCard>

          <WidgetCard delay="0.8s">
            <WidgetTitle>Latest Applications</WidgetTitle>
            <ListContainer>
              {recentApps.length > 0 ? (
                recentApps.map((app) => (
                  <ListItem key={app.id}>
                    <div>
                      <ItemMain>
                        {app.candidate?.firstName} {app.candidate?.lastName}
                      </ItemMain>
                      <ItemSub>
                        Applied for: {app.job?.title || "Unknown"}
                      </ItemSub>
                    </div>
                    <DateText>{Moment(app.createdDate).fromNow()}</DateText>
                  </ListItem>
                ))
              ) : (
                <EmptyText>No recent applications.</EmptyText>
              )}
            </ListContainer>
          </WidgetCard>
        </Column>
      </WidgetsGrid>
    </Section>
  );
};

// --- ANIMATIONS & STYLED COMPONENTS ---

const slideUpFade = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const HeaderSection = styled.div`
  margin-bottom: 2rem;
  animation: ${slideUpFade} 0.4s ease-out forwards;
`;

const SubGreeting = styled.p`
  font-size: 15px;
  color: #6b7280;
  margin: 0;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 1.5rem;
  margin-bottom: 1.5rem;
`;

const StatCard = styled.div`
  background: white;
  padding: 24px;
  border-radius: 12px;
  box-shadow: ${shadow.light};
  opacity: 0;
  animation: ${slideUpFade} 0.5s ease-out forwards;
  animation-delay: ${(props) => props.delay || "0s"};
  border: 1px solid rgba(0, 0, 0, 0.02);
`;

const StatHeader = styled.div`
  display: flex;
  margin-bottom: 12px;
`;

const IconWrapper = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 10px;
  background-color: ${(props) => props.bg};
  color: ${(props) => props.color};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 22px;
`;

const StatValue = styled.h2`
  font-size: 28px;
  font-weight: 700;
  color: #111;
  margin: 0 0 4px 0;
`;

const StatLabel = styled.p`
  font-size: 14px;
  color: #666;
  margin: 0;
`;

const WidgetsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const Column = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const WidgetCard = styled.div`
  background: white;
  padding: 24px;
  border-radius: 12px;
  box-shadow: ${shadow.light};
  opacity: 0;
  animation: ${slideUpFade} 0.5s ease-out forwards;
  animation-delay: ${(props) => props.delay || "0s"};
  border: 1px solid rgba(0, 0, 0, 0.02);
`;

const WidgetTitle = styled.h3`
  margin: 0 0 16px 0;
  font-size: 18px;
  color: #333;
  padding-bottom: 12px;
  border-bottom: 1px solid #f0f0f0;
`;

const AboutText = styled.p`
  color: #4b5563;
  line-height: 1.6;
  font-size: 15px;
  margin-bottom: 1rem;
`;

const MetaFlex = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
`;

const MetaTag = styled.span`
  background: #f3f4f6;
  color: #374151;
  padding: 6px 12px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 500;
`;

const ListContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const ListItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px;
  background: #f8f9fa;
  border-radius: 8px;
  transition: background 0.2s;

  &:hover {
    background: #f1f3f5;
  }
`;

const ItemMain = styled.h4`
  margin: 0 0 4px 0;
  font-size: 14px;
  color: #111;
  font-weight: 600;
`;

const ItemSub = styled.p`
  margin: 0;
  font-size: 12px;
  color: #666;
`;

const Badge = styled.span`
  background: ${(props) => props.bg};
  color: ${(props) => props.color};
  padding: 4px 10px;
  border-radius: 50px;
  font-size: 11px;
  font-weight: bold;
`;

const DateText = styled.span`
  font-size: 12px;
  color: #888;
`;

const EmptyText = styled.p`
  color: #9ca3af;
  font-size: 14px;
  font-style: italic;
  margin: 0;
`;
